import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { DollarSign, Calendar, Info, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../services/api';

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BalanceSection = styled.div`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const BalanceLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
`;

const BalanceAmount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const BalanceInfo = styled.div`
  font-size: 0.85rem;
  opacity: 0.85;
`;

const CreditsListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const CreditsListTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CreditsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CreditItem = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s;

  &:hover {
    border-color: #00d4aa;
    box-shadow: 0 2px 8px rgba(0, 212, 170, 0.1);
  }
`;

const CreditHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const CreditInfo = styled.div`
  flex: 1;
`;

const CreditDescription = styled.div`
  font-weight: 500;
  color: #333;
  margin-bottom: 0.25rem;
`;

const CreditMeta = styled.div`
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CreditAmount = styled.div`
  text-align: right;
`;

const CreditValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #00d4aa;
`;

const CreditUsed = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 0.75rem;
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  background: linear-gradient(90deg, #00d4aa 0%, #00b894 100%);
  width: ${props => props.$percentage}%;
  transition: width 0.3s ease;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const InfoBox = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #00d4aa;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #666;
  display: flex;
  gap: 0.75rem;
`;

const Badge = styled.span<{ $color?: string }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$color || '#e9ecef'};
  color: white;
`;

interface Credit {
  id: number;
  amount: number;
  amount_remaining: number;
  amount_used: number;
  source: string;
  description: string;
  expires_at: string | null;
  expires_in_days: number | null;
  created_at: string;
  formatted_amount: string;
  formatted_remaining: string;
}

interface CreditsData {
  total_balance: number;
  formatted_balance: string;
  active_credits: Credit[];
  active_credits_count: number;
}

export const AccountCreditsCard: React.FC = () => {
  const [creditsData, setCreditsData] = useState<CreditsData | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCredits();
  }, []);

  const loadCredits = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getCreditsBalance();

      if (response.success) {
        setCreditsData(response.data);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSourceBadgeColor = (source: string): string => {
    switch (source) {
      case 'downgrade':
        return '#00d4aa';
      case 'refund':
        return '#ffc107';
      case 'bonus':
        return '#9b59b6';
      default:
        return '#6c757d';
    }
  };

  const getSourceLabel = (source: string): string => {
    const labels: Record<string, string> = {
      'downgrade': 'Downgrade',
      'refund': 'Reembolso',
      'bonus': 'Bônus',
      'promotion': 'Promoção'
    };
    return labels[source] || source;
  };

  if (isLoading) {
    return (
      <Card>
        <Header>
          <Title>
            <DollarSign size={24} />
            Seus Créditos
          </Title>
        </Header>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Carregando...
        </div>
      </Card>
    );
  }

  if (!creditsData || creditsData.total_balance === 0) {
    return (
      <Card>
        <Header>
          <Title>
            <DollarSign size={24} />
            Seus Créditos
          </Title>
        </Header>
        <EmptyState>
          <DollarSign size={48} style={{ opacity: 0.3, margin: '0 auto 1rem' }} />
          <p>Você não possui créditos disponíveis no momento.</p>
          <InfoBox>
            <Info size={20} />
            <div>
              <strong>Como ganhar créditos?</strong><br />
              Ao fazer downgrade de plano, o valor proporcional dos dias restantes é convertido em créditos que podem ser usados em pagamentos futuros.
            </div>
          </InfoBox>
        </EmptyState>
      </Card>
    );
  }

  return (
    <Card>
      <Header>
        <Title>
          <DollarSign size={24} />
          Seus Créditos
        </Title>
      </Header>

      <BalanceSection>
        <BalanceLabel>Saldo Total Disponível</BalanceLabel>
        <BalanceAmount>{creditsData.formatted_balance}</BalanceAmount>
        <BalanceInfo>
          {creditsData.active_credits_count} {creditsData.active_credits_count === 1 ? 'crédito ativo' : 'créditos ativos'}
        </BalanceInfo>
      </BalanceSection>

      <CreditsListHeader onClick={() => setIsExpanded(!isExpanded)}>
        <CreditsListTitle>
          Detalhes dos Créditos
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </CreditsListTitle>
      </CreditsListHeader>

      {isExpanded && (
        <CreditsList>
          {creditsData.active_credits.map((credit) => {
            const usedPercentage = ((credit.amount_used / credit.amount) * 100);
            const remainingPercentage = 100 - usedPercentage;

            return (
              <CreditItem key={credit.id}>
                <CreditHeader>
                  <CreditInfo>
                    <CreditDescription>{credit.description}</CreditDescription>
                    <CreditMeta>
                      <Badge $color={getSourceBadgeColor(credit.source)}>
                        {getSourceLabel(credit.source)}
                      </Badge>
                      {credit.expires_at && (
                        <>
                          <Calendar size={14} />
                          <span>
                            Expira em {credit.expires_in_days} {credit.expires_in_days === 1 ? 'dia' : 'dias'}
                          </span>
                        </>
                      )}
                    </CreditMeta>
                  </CreditInfo>
                  <CreditAmount>
                    <CreditValue>{credit.formatted_remaining}</CreditValue>
                    <CreditUsed>
                      de {credit.formatted_amount}
                    </CreditUsed>
                  </CreditAmount>
                </CreditHeader>
                <ProgressBar>
                  <ProgressFill $percentage={remainingPercentage} />
                </ProgressBar>
              </CreditItem>
            );
          })}
        </CreditsList>
      )}

      <InfoBox>
        <Info size={20} />
        <div>
          <strong>Como usar seus créditos?</strong><br />
          Seus créditos serão aplicados automaticamente no próximo pagamento. Os créditos mais próximos do vencimento são usados primeiro.
        </div>
      </InfoBox>
    </Card>
  );
};
